'use client';
import React, { useEffect, useRef, useState } from 'react';

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref?.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          obs.unobserve(el);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    obs?.observe(el);
    return () => obs?.disconnect();
  }, [delay]);
  return { ref, visible };
}

export default function ContentSection() {
  const heading = useReveal(0);
  const card1 = useReveal(0);
  const card2 = useReveal(100);
  const pill1 = useReveal(0);
  const pill2 = useReveal(120);
  const pill3 = useReveal(240);
  const marquee = useReveal(0);

  return (
    <section className="bg-white overflow-hidden">
      {/* ── Section label + heading ── */}
      <div
        ref={heading?.ref}
        className="max-w-screen-xl mx-auto px-6 md:px-16 pt-20 md:pt-32 pb-12 md:pb-16 transition-all duration-700 ease-out"
        style={{ opacity: heading?.visible ? 1 : 0, transform: heading?.visible ? 'translateY(0)' : 'translateY(48px)' }}
      >
        <div className="flex items-center gap-4 mb-6">
          <span className="w-10 h-px bg-brand" />
          <span className="text-brand text-xs font-bold uppercase tracking-[0.25em]">Over ons</span>
        </div>
        <h2 className="font-black text-gray-900 leading-none max-w-3xl" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)' }}>
          Rijles die écht<br />
          <span style={{ color: 'transparent', WebkitTextStroke: '2px #111827' }}>bij jou past</span>
        </h2>
      </div>
      {/* ── Main two-column block ── */}
      <div className="max-w-screen-xl mx-auto px-6 md:px-16 pb-12 grid md:grid-cols-2 gap-5">

        {/* Left: text card */}
        <div
          ref={card1?.ref}
          className="bg-gray-950 text-white p-10 md:p-14 flex flex-col justify-between min-h-[420px] transition-all duration-700 ease-out"
          style={{ opacity: card1?.visible ? 1 : 0, transform: card1?.visible ? 'translateX(0)' : 'translateX(-60px)' }}
        >
          <div>
            <p className="text-gray-300 text-lg leading-relaxed mb-5">
              Rijschool Simson is gevestigd in <strong className="text-white">Almere</strong>. Stephen Simson is een jonge, enthousiaste rijinstructeur die je op je gemak laat voelen — van je allereerste les tot het examen.
            </p>
            <p className="text-gray-400 leading-relaxed">
              Maatwerk staat centraal: lessen worden volledig afgestemd op jouw tempo en behoeften. Met avondlessen, flexibele tijden en een persoonlijke aanpak zorgen we dat rijden leren voor iedereen haalbaar is.
            </p>
          </div>
          <a
            href="https://driveplannen.nl/registreer/46ec6865-5dc5-4e1c-a646-ce961abc1633"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-brand text-white font-bold px-7 py-4 text-sm uppercase tracking-widest hover:bg-brand/90 active:scale-95 transition-all duration-150 self-start mt-8"
          >
            Plan je proefles
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
            </svg>
          </a>
        </div>

        {/* Right: stats card */}
        <div
          ref={card2?.ref}
          className="bg-gray-50 p-10 md:p-14 flex flex-col justify-between min-h-[420px] transition-all duration-700 ease-out"
          style={{ opacity: card2?.visible ? 1 : 0, transform: card2?.visible ? 'translateX(0)' : 'translateX(60px)' }}
        >
          <div className="text-brand font-bold text-xs uppercase tracking-[0.25em] mb-8">Waarom Simson?</div>
          <div className="space-y-8 flex-1">
            {[
              { stat: '100%', label: 'Vaste instructeur — altijd dezelfde begeleider' },
              { stat: 'Flex', label: 'Avond- & weekendlessen op jouw schema' },
              { stat: 'App', label: 'Lessen plannen & voortgang bijhouden' },
            ]?.map((item) => (
              <div key={item?.label} className="flex items-start gap-5 border-b border-gray-200 pb-6 last:border-0 last:pb-0">
                <div className="text-brand font-black text-3xl w-20 shrink-0 leading-none">{item?.stat}</div>
                <div className="text-gray-600 text-sm leading-relaxed pt-1">{item?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* ── Feature pills row ── */}
      <div className="max-w-screen-xl mx-auto px-6 md:px-16 pb-20 md:pb-32 grid md:grid-cols-3 gap-5">
        {[
          { ref: pill1, icon: '🎯', title: 'Persoonlijk', desc: 'Elke les afgestemd op jouw niveau en tempo', delay: 0 },
          { ref: pill2, icon: '💰', title: 'Betaalbaar', desc: 'Eerlijke tarieven, betalen in termijnen mogelijk', delay: 120 },
          { ref: pill3, icon: '🏆', title: 'Resultaatgericht', desc: 'Gericht op een hoge slagingskans bij het examen', delay: 240 },
        ]?.map((f, i) => (
          <div
            key={f?.title}
            ref={f?.ref?.ref}
            className="border border-gray-100 p-8 hover:border-brand/40 hover:bg-gray-50 transition-all duration-300 group"
            style={{
              opacity: f?.ref?.visible ? 1 : 0,
              transform: f?.ref?.visible ? 'translateY(0)' : 'translateY(40px)',
              transition: `opacity 0.6s ease ${i * 0.12}s, transform 0.6s ease ${i * 0.12}s, border-color 0.3s, background-color 0.3s`,
            }}
          >
            <div className="text-3xl mb-5">{f?.icon}</div>
            <div className="font-black text-gray-900 text-xl mb-2">{f?.title}</div>
            <div className="text-gray-500 text-sm leading-relaxed">{f?.desc}</div>
          </div>
        ))}
      </div>
      {/* ── Marquee strip ── */}
      <div
        ref={marquee?.ref}
        className="bg-brand py-4 overflow-hidden transition-all duration-700 ease-out"
        style={{ opacity: marquee?.visible ? 1 : 0 }}
      >
        <div className="flex gap-0 animate-marquee whitespace-nowrap">
          {Array(6)?.fill(null)?.map((_, i) => (
            <span key={i} className="text-white font-black text-sm uppercase tracking-widest px-8 shrink-0">
              Rijschool Simson — Almere — Persoonlijke rijlessen — Hoge slagingskans — Flexibele tijden —&nbsp;
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
